/**
 * The data source with no service behind it.
 *
 * WHY THIS EXISTS
 * Every other dashboard source answers a widget by asking a server: GeoServer
 * gets a WFS request, ArcGIS gets a `query`. A layer imported from a file has
 * nobody to ask — which is why file-backed layers used to be offered with
 * "there is no service for a dashboard to query" and could not be used at all.
 * This module IS that service, running in the browser over features the
 * dashboard carries with it.
 *
 * It implements the same `DataSourceAdapter` contract as the other two, so
 * every widget, filter, action and field picker works against a shapefile
 * exactly as it does against a published layer. Nothing above this file knows
 * the difference.
 *
 * THE `where` PROBLEM, AND WHY THERE IS A PARSER HERE
 * The adapter contract passes filters as a STRING, because that is what a
 * service consumes. A browser cannot post a string to itself, so it has to
 * understand it — hence `evaluateWhere` below.
 *
 * That is only reasonable because the grammar is CLOSED: this app generates
 * every expression it will ever have to read, in exactly two places —
 * `buildWhereClause` (the widget's own filters) and `actionRuntime` (what one
 * widget contributes to another). Between them they emit comparisons, LIKE,
 * IS NULL / IS NOT NULL, IN lists, and AND/OR with parentheses. Nothing else.
 * So this is a small recursive-descent parser over a known language, not an
 * attempt at SQL.
 *
 * An expression it cannot parse REFUSES the whole query rather than quietly
 * returning everything. A filter that silently fails open is the worst of both
 * worlds: the numbers are wrong and the board looks fine.
 */

import type {
  CategoryQuery,
  CategoryResult,
  DataSourceAdapter,
  Extent,
  RecordQuery,
  RecordResult,
  StatisticQuery,
  StatisticResult,
} from './dashboardDataService';
import { aggregate, groupAndAggregate, sortCategories } from '../utils/dashboardUtils';
import type { DashboardDataSource, DashboardField } from '../types/dashboardTypes';
import type { LayerGeometryType } from '../../types';

/* ─────────────────────────── Expression parsing ─────────────────────────── */

type Predicate = (record: Record<string, unknown>) => boolean;

/** One lexical token of the closed grammar described in the file note. */
interface Token {
  kind: 'word' | 'string' | 'number' | 'punct';
  text: string;
}

class ParseError extends Error {}

function tokenise(input: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < input.length) {
    const char = input[index];

    if (/\s/.test(char)) {
      index += 1;
      continue;
    }

    if (char === "'") {
      // SQL escapes a quote by doubling it, which is what `quote()` in
      // dashboardUtils emits — so O'Brien arrives as 'O''Brien'.
      let value = '';
      index += 1;
      while (index < input.length) {
        if (input[index] === "'") {
          if (input[index + 1] === "'") {
            value += "'";
            index += 2;
            continue;
          }
          index += 1;
          break;
        }
        value += input[index];
        index += 1;
      }
      tokens.push({ kind: 'string', text: value });
      continue;
    }

    // `>=`, `<=` and `<>` must be read before the single-character operators,
    // or `>=` becomes `>` followed by a stray `=`.
    const pair = input.slice(index, index + 2);
    if (pair === '>=' || pair === '<=' || pair === '<>' || pair === '!=') {
      tokens.push({ kind: 'punct', text: pair === '!=' ? '<>' : pair });
      index += 2;
      continue;
    }

    if ('()=<>,'.includes(char)) {
      tokens.push({ kind: 'punct', text: char });
      index += 1;
      continue;
    }

    if (/[\d.-]/.test(char) && /[\d.]/.test(input[index + 1] ?? char)) {
      let value = '';
      while (index < input.length && /[\d.eE+-]/.test(input[index])) {
        value += input[index];
        index += 1;
      }
      tokens.push({ kind: 'number', text: value });
      continue;
    }

    // A bare word: a field name, or a keyword such as AND / IS / LIKE.
    let word = '';
    while (index < input.length && /[^\s()=<>,']/.test(input[index])) {
      word += input[index];
      index += 1;
    }
    if (word === '') throw new ParseError(`Unexpected character "${char}"`);
    tokens.push({ kind: 'word', text: word });
  }

  return tokens;
}

/** Reads a record's field, tolerating the case a service would have folded. */
function readField(record: Record<string, unknown>, field: string): unknown {
  if (field in record) return record[field];
  const lowered = field.toLowerCase();
  const match = Object.keys(record).find((key) => key.toLowerCase() === lowered);
  return match === undefined ? undefined : record[match];
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

const MONTH_NAMES: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

function dateFromParts(year: number, monthIndex: number, day: number, hour = 0, minute = 0): number | null {
  if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
  const timestamp = Date.UTC(year, monthIndex, day, hour, minute);
  const date = new Date(timestamp);
  // Rejects overflow (e.g. day 31 in a 30-day month rolling into next month)
  // rather than silently accepting a date that isn't the one written.
  return date.getUTCFullYear() === year && date.getUTCMonth() === monthIndex && date.getUTCDate() === day
    ? timestamp
    : null;
}

/**
 * Reads a date out of a string in whatever common spreadsheet format it was
 * written in, as an epoch timestamp — or null if it isn't a date at all.
 *
 * A file's date column has no declared type by the time features reach here
 * (see `inferType` below), and Excel's own "format as date" only fixes how a
 * cell DISPLAYS — exporting to CSV bakes in whatever locale format was on
 * screen, e.g. `12-Oct-2025`, `12/10/2025` or `12-10-2025` all for the same
 * day. This is the one place that has to read all of them, since it backs
 * both type inference and comparing a record's own value against a header
 * Date selector's ISO bound.
 */
function parseFlexibleDate(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw) return null;

  // ISO or ISO-like: 2025-10-12, 2025/10/12, 2025-10-12T14:30
  let match = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T ](\d{1,2}):(\d{2}))?$/);
  if (match) {
    const [, year, month, day, hour, minute] = match;
    return dateFromParts(Number(year), Number(month) - 1, Number(day), Number(hour ?? 0), Number(minute ?? 0));
  }

  // Day-then-month-name: 12-Oct-2025, 12 Oct 2025
  match = raw.match(/^(\d{1,2})[-\s]([A-Za-z]{3,9})[-\s](\d{4})$/);
  if (match) {
    const [, day, monthName, year] = match;
    const monthIndex = MONTH_NAMES[monthName.slice(0, 3).toLowerCase()];
    return monthIndex === undefined ? null : dateFromParts(Number(year), monthIndex, Number(day));
  }

  // Numeric day/month/year, either separator: 12-10-2025, 12/10/2025.
  // Read day-first (the format every example the field actually arrives in
  // uses), but fall back to month-first when day-first is impossible — e.g.
  // 10/25/2025 can only be month 10, day 25.
  match = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (match) {
    const [, first, second, year] = match;
    let day = Number(first);
    let month = Number(second);
    if (month > 12 && day <= 12) [day, month] = [month, day];
    return dateFromParts(Number(year), month - 1, day);
  }

  return null;
}

function looksLikeDate(value: unknown): boolean {
  return parseFlexibleDate(value) !== null;
}

/**
 * Compares two values the way the services this replaces would.
 *
 * Numeric when BOTH sides look numeric, so `POP > 1000` behaves like a number
 * comparison and `WARD > 'A'` behaves like a string one — which is what the
 * expression meant in each case, and what ArcGIS and GeoServer each do. Tried
 * as a date next, before falling back to text: a header Date selector always
 * emits an ISO bound (`FIELD >= '2025-10-12'`), so a column actually stored as
 * `12-Oct-2025` needs both sides read as real dates or every comparison would
 * be a meaningless string compare between two different formats.
 */
function compare(left: unknown, right: unknown): number | null {
  if (isBlank(left) || isBlank(right)) return null;

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber === rightNumber ? 0 : leftNumber < rightNumber ? -1 : 1;
  }

  const leftDate = parseFlexibleDate(left);
  const rightDate = parseFlexibleDate(right);
  if (leftDate !== null && rightDate !== null) {
    return leftDate === rightDate ? 0 : leftDate < rightDate ? -1 : 1;
  }

  const leftText = String(left);
  const rightText = String(right);
  return leftText === rightText ? 0 : leftText < rightText ? -1 : 1;
}

/** SQL LIKE, limited to the `%` wildcard the filter UI can actually produce. */
function likeMatcher(pattern: string): (value: unknown) => boolean {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/%/g, '.*');
  const regex = new RegExp(`^${escaped}$`, 'i');
  return (value) => !isBlank(value) && regex.test(String(value));
}

class Parser {
  private position = 0;

  constructor(private readonly tokens: Token[]) {}

  private peek(): Token | undefined {
    return this.tokens[this.position];
  }

  private next(): Token {
    const token = this.tokens[this.position];
    if (!token) throw new ParseError('Expression ended unexpectedly');
    this.position += 1;
    return token;
  }

  private takePunct(text: string): void {
    const token = this.next();
    if (token.kind !== 'punct' || token.text !== text) {
      throw new ParseError(`Expected "${text}" but found "${token.text}"`);
    }
  }

  private peekKeyword(word: string): boolean {
    const token = this.peek();
    return Boolean(token && token.kind === 'word' && token.text.toUpperCase() === word);
  }

  parse(): Predicate {
    const predicate = this.parseOr();
    if (this.position < this.tokens.length) {
      throw new ParseError(`Unexpected "${this.tokens[this.position].text}"`);
    }
    return predicate;
  }

  private parseOr(): Predicate {
    let left = this.parseAnd();
    while (this.peekKeyword('OR')) {
      this.next();
      const right = this.parseAnd();
      const previous = left;
      left = (record) => previous(record) || right(record);
    }
    return left;
  }

  private parseAnd(): Predicate {
    let left = this.parseTerm();
    while (this.peekKeyword('AND')) {
      this.next();
      const right = this.parseTerm();
      const previous = left;
      left = (record) => previous(record) && right(record);
    }
    return left;
  }

  private parseTerm(): Predicate {
    const token = this.peek();
    if (!token) throw new ParseError('Expression ended unexpectedly');

    if (token.kind === 'punct' && token.text === '(') {
      this.next();
      const inner = this.parseOr();
      this.takePunct(')');
      return inner;
    }

    if (token.kind === 'word' && token.text.toUpperCase() === 'NOT') {
      this.next();
      const inner = this.parseTerm();
      return (record) => !inner(record);
    }

    return this.parseComparison();
  }

  private parseComparison(): Predicate {
    const fieldToken = this.next();
    if (fieldToken.kind !== 'word' && fieldToken.kind !== 'string') {
      throw new ParseError(`Expected a field name but found "${fieldToken.text}"`);
    }
    // GeoServer quotes reserved words as "SHAPE"; strip that if it appears.
    const field = fieldToken.text.replace(/^"(.*)"$/, '$1');

    const operatorToken = this.next();

    if (operatorToken.kind === 'word') {
      const keyword = operatorToken.text.toUpperCase();

      if (keyword === 'IS') {
        let negated = false;
        if (this.peekKeyword('NOT')) {
          this.next();
          negated = true;
        }
        const nullToken = this.next();
        if (nullToken.text.toUpperCase() !== 'NULL') {
          throw new ParseError(`Expected NULL but found "${nullToken.text}"`);
        }
        return (record) => {
          const blank = isBlank(readField(record, field));
          return negated ? !blank : blank;
        };
      }

      if (keyword === 'LIKE' || keyword === 'ILIKE') {
        const pattern = this.next();
        const matches = likeMatcher(pattern.text);
        return (record) => matches(readField(record, field));
      }

      if (keyword === 'IN') {
        this.takePunct('(');
        const values: string[] = [];
        for (;;) {
          const value = this.next();
          if (value.kind === 'punct') throw new ParseError('Empty value in IN list');
          values.push(value.text);
          const separator = this.peek();
          if (separator && separator.kind === 'punct' && separator.text === ',') {
            this.next();
            continue;
          }
          break;
        }
        this.takePunct(')');
        return (record) => {
          const actual = readField(record, field);
          return values.some((value) => compare(actual, value) === 0);
        };
      }

      throw new ParseError(`Unsupported operator "${operatorToken.text}"`);
    }

    if (operatorToken.kind !== 'punct') {
      throw new ParseError(`Expected an operator but found "${operatorToken.text}"`);
    }

    const valueToken = this.next();
    const expected = valueToken.text;
    const operator = operatorToken.text;

    return (record) => {
      const order = compare(readField(record, field), expected);
      if (order === null) {
        // NULL compares to nothing, exactly as it does in SQL — so a row with
        // no value is excluded rather than counted as "not equal".
        return false;
      }
      switch (operator) {
        case '=':
          return order === 0;
        case '<>':
          return order !== 0;
        case '>':
          return order > 0;
        case '<':
          return order < 0;
        case '>=':
          return order >= 0;
        case '<=':
          return order <= 0;
        default:
          return false;
      }
    };
  }
}

/**
 * Compiles a `where` expression into a predicate.
 *
 * THROWS on anything it cannot read. The caller turns that into a visible
 * error: a filter that silently matched everything would leave a widget
 * showing a confident, wrong number.
 */
export function compileWhere(expression: string | undefined): Predicate | null {
  const trimmed = (expression ?? '').trim();
  if (!trimmed) return null;
  try {
    return new Parser(tokenise(trimmed)).parse();
  } catch (error) {
    throw new Error(
      `This filter could not be applied to an imported layer: ${
        error instanceof Error ? error.message : 'unrecognised expression'
      }`,
    );
  }
}

/* ──────────────────────────── Spatial narrowing ─────────────────────────── */

/** Every coordinate pair in a geometry, whatever its nesting. */
function* coordinatesOf(geometry: GeoJSON.Geometry | null | undefined): Generator<[number, number]> {
  if (!geometry) return;
  const walk = function* (value: any): Generator<[number, number]> {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number' && typeof value[1] === 'number') {
      yield [value[0], value[1]];
      return;
    }
    for (const part of value) yield* walk(part);
  };
  yield* walk((geometry as any).coordinates);
}

export function featureBounds(feature: GeoJSON.Feature): Extent | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  for (const [x, y] of coordinatesOf(feature.geometry)) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    west = Math.min(west, x);
    east = Math.max(east, x);
    south = Math.min(south, y);
    north = Math.max(north, y);
  }

  return Number.isFinite(west) && Number.isFinite(south) ? [west, south, east, north] : null;
}

/**
 * Whether a feature falls inside a map widget's extent.
 *
 * Bounding-box overlap, which is what the WFS and ArcGIS paths request too
 * (`BBOX` and `esriSpatialRelIntersects` against an envelope) — so a map that
 * drives a chart narrows it the same way whatever the source is.
 */
function withinExtent(feature: GeoJSON.Feature, extent: Extent): boolean {
  const bounds = featureBounds(feature);
  if (!bounds) return false;
  return !(bounds[2] < extent[0] || bounds[0] > extent[2] || bounds[3] < extent[1] || bounds[1] > extent[3]);
}

/**
 * Whether a feature falls inside a selected SHAPE.
 *
 * Also bounding-box, deliberately. The services do true geometric
 * intersection, and matching that in the browser would mean a polygon-clipping
 * library for a case that only arises when a map widget drives another widget
 * by selection. The envelope is the honest approximation, and it errs towards
 * including a feature rather than dropping one.
 */
function withinGeometry(feature: GeoJSON.Feature, geometry: GeoJSON.Geometry): boolean {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const [x, y] of coordinatesOf(geometry)) {
    west = Math.min(west, x);
    east = Math.max(east, x);
    south = Math.min(south, y);
    north = Math.max(north, y);
  }
  if (!Number.isFinite(west)) return false;
  return withinExtent(feature, [west, south, east, north]);
}

/* ───────────────────────────── Querying ─────────────────────────────────── */

function featuresOf(source: DashboardDataSource): GeoJSON.Feature[] {
  return source.localData?.features ?? [];
}

/** The features left after everything the query constrains. */
function selectFeatures(
  source: DashboardDataSource,
  query: { where?: string; extent?: Extent; geometry?: GeoJSON.Geometry },
): GeoJSON.Feature[] {
  const predicate = compileWhere(query.where);
  let features = featuresOf(source);

  if (predicate) {
    features = features.filter((feature) =>
      predicate((feature.properties ?? {}) as Record<string, unknown>),
    );
  }
  if (query.extent) {
    features = features.filter((feature) => withinExtent(feature, query.extent as Extent));
  }
  if (query.geometry) {
    features = features.filter((feature) =>
      withinGeometry(feature, query.geometry as GeoJSON.Geometry),
    );
  }
  return features;
}

function recordsOf(features: GeoJSON.Feature[]): Record<string, unknown>[] {
  return features.map((feature) => (feature.properties ?? {}) as Record<string, unknown>);
}

/**
 * The type of a column, inferred from the values actually present.
 *
 * A file carries no schema — a shapefile's .dbf has one, but by the time the
 * features reach here they are plain GeoJSON properties. So the type is read
 * from the data, which is what decides whether a widget offers Sum and Average
 * on this column or only Count.
 */
function inferType(values: unknown[]): DashboardField['type'] {
  const present = values.filter((value) => !isBlank(value));
  if (present.length === 0) return 'string';

  if (present.every((value) => typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))))) {
    return 'number';
  }

  // The GDB importer emits real ISO dates; a CSV built by hand in Excel
  // carries whatever the cell's display format baked in instead — see
  // `parseFlexibleDate`'s own note on the formats that shows up as.
  if (present.every(looksLikeDate)) return 'date';

  return 'string';
}

/** Columns present on a local source, sampled rather than fully scanned. */
export function localFields(source: DashboardDataSource): DashboardField[] {
  const features = featuresOf(source);
  // Enough rows to see a column that is blank at the top of the file, without
  // walking a million features to build a field list.
  const sample = features.slice(0, 500);

  const names: string[] = [];
  const seen = new Set<string>();
  for (const feature of sample) {
    for (const key of Object.keys(feature.properties ?? {})) {
      if (!seen.has(key)) {
        seen.add(key);
        names.push(key);
      }
    }
  }

  return names.map((name) => ({
    name,
    alias: name,
    type: inferType(sample.map((feature) => (feature.properties ?? {})[name])),
  }));
}

export const localAdapter: DataSourceAdapter = {
  async fetchFields(source) {
    return localFields(source);
  },

  async fetchStatistic(source, query: StatisticQuery): Promise<StatisticResult> {
    const features = selectFeatures(source, query);
    if (query.statistic === 'count') {
      return { value: features.length, truncated: false };
    }
    const values = recordsOf(features).map((record) =>
      query.field ? readField(record, query.field) : null,
    );
    // Never truncated: the whole layer is already in memory, so unlike the
    // service adapters there is no capped read to warn about.
    return { value: aggregate(values, query.statistic), truncated: false };
  },

  async fetchCategories(source, query: CategoryQuery): Promise<CategoryResult> {
    const records = recordsOf(selectFeatures(source, query));
    const grouped = groupAndAggregate(
      records,
      query.categoryField,
      query.statistic,
      query.field,
    );
    const sorted = sortCategories(grouped, query.sortBy);
    return {
      data: query.limit ? sorted.slice(0, query.limit) : sorted,
      truncated: false,
    };
  },

  async fetchRecords(source, query: RecordQuery): Promise<RecordResult> {
    const features = selectFeatures(source, query);
    let records = recordsOf(features);
    const total = records.length;

    if (query.orderByField) {
      const field = query.orderByField;
      const direction = query.orderDirection === 'desc' ? -1 : 1;
      records = [...records].sort((a, b) => {
        const order = compare(readField(a, field), readField(b, field));
        // Rows with no value sort last in both directions, so a descending
        // sort does not open with a screen of blanks.
        if (order === null) return isBlank(readField(a, field)) ? 1 : -1;
        return order * direction;
      });
    }

    if (query.limit) records = records.slice(0, query.limit);

    if (query.columns && query.columns.length > 0) {
      const columns = query.columns;
      records = records.map((record) => {
        const trimmed: Record<string, unknown> = {};
        for (const column of columns) trimmed[column] = readField(record, column);
        return trimmed;
      });
    }

    return { records, total };
  },

  async fetchDistinctValues(source, field) {
    const seen = new Set<string>();
    for (const record of recordsOf(featuresOf(source))) {
      const value = readField(record, field);
      if (isBlank(value)) continue;
      seen.add(String(value));
      // The filter editor offers these as suggestions; a thousand of them is
      // already more than anyone scrolls.
      if (seen.size >= 1000) break;
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  },
};

/* ─────────────────────── Map-side helpers ───────────────────────────────── */

/** The extent of everything in a local source, for framing a map widget. */
export function localSourceExtent(source: DashboardDataSource): Extent | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  for (const feature of featuresOf(source)) {
    const bounds = featureBounds(feature);
    if (!bounds) continue;
    west = Math.min(west, bounds[0]);
    south = Math.min(south, bounds[1]);
    east = Math.max(east, bounds[2]);
    north = Math.max(north, bounds[3]);
  }

  return Number.isFinite(west) && Number.isFinite(south) ? [west, south, east, north] : null;
}

/** The features a selection action should highlight on the map. */
export function localMatchingGeometry(
  source: DashboardDataSource,
  where: string | undefined,
  limit: number,
): GeoJSON.FeatureCollection {
  const features = selectFeatures(source, { where })
    .filter((feature) => feature.geometry)
    .slice(0, limit);
  return { type: 'FeatureCollection', features };
}

/** Geometry kind, read off the features rather than declared by a service. */
export function localGeometryType(
  data: GeoJSON.FeatureCollection | undefined,
): LayerGeometryType {
  for (const feature of data?.features ?? []) {
    const type = (feature.geometry?.type ?? '').toLowerCase();
    if (type.includes('point')) return 'point';
    if (type.includes('linestring')) return 'line';
    if (type.includes('polygon')) return 'polygon';
  }
  return 'unknown';
}
